import { useMemo } from "react";
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Hash, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  IssueDetail,
  IssueDetailSkeleton,
  IssueNotFound,
  type IssueDetailScope,
} from "@/components/issues/detail/issue-detail";
import { useCommands, type AppCommand } from "@/components/shell/commands";
import { PageBody, PageHeader, QueryBoundary } from "@/components/shell/page";
import { useProject, useWorkspace } from "@/components/shell/workspace-context";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/$workspaceSlug/$projectSlug/issues/$number")({
  component: IssuePage,
});

/**
 * The number in an issue's address, or null for anything else. Digits only,
 * with no leading zero, so an issue has one address.
 */
function issueNumber(raw: string): number | null {
  return /^[1-9]\d{0,14}$/.test(raw) ? Number(raw) : null;
}

function IssuePage() {
  const { t } = useTranslation("issues");
  const { t: tShell } = useTranslation("shell");
  const { workspaceSlug, projectSlug, number: raw } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const project = useProject();
  const number = issueNumber(raw);

  const commands = useMemo<AppCommand[]>(
    () => [
      {
        id: "issue.back",
        title: t("detail.back"),
        group: "navigation",
        keys: "escape",
        hidden: true,
        // Back to where the issue was opened from, with its view, its
        // filters and its place. An issue opened from a link has no such
        // place, and goes to the list.
        perform: () => {
          if (canGoBack) router.history.back();
          else {
            void navigate({
              to: "/$workspaceSlug/$projectSlug/issues",
              params: { workspaceSlug, projectSlug },
            });
          }
        },
      },
    ],
    [t, navigate, router, canGoBack, workspaceSlug, projectSlug],
  );
  useCommands(commands);

  return (
    <>
      <PageHeader
        crumbs={[
          { label: project.name, to: "/$workspaceSlug/$projectSlug" },
          { label: tShell("nav.issues"), to: "/$workspaceSlug/$projectSlug/issues" },
        ]}
        title={number === null ? t("detail.title") : `${project.key}-${number}`}
      />
      {number === null ? (
        <PageBody>
          <IssueNotFound />
        </PageBody>
      ) : (
        <QueryBoundary resetKey={`${project._id}:${number}`}>
          <NumberedIssue number={number} />
        </QueryBoundary>
      )}
    </>
  );
}

/** Finds the issue behind the number, then shows it. */
function NumberedIssue({ number }: { number: number }) {
  const project = useProject();
  const { workspace } = useWorkspace();
  const issueId = useQuery(api.issues.byNumber, { projectId: project._id, number });
  const scope = useMemo<IssueDetailScope>(
    () => ({
      projectId: project._id,
      workspaceId: workspace._id,
      workspaceSlug: workspace.slug,
      projectSlug: project.slug,
    }),
    [project._id, project.slug, workspace._id, workspace.slug],
  );

  if (issueId === undefined) return <IssueDetailSkeleton />;
  if (issueId === null) {
    return (
      <PageBody>
        <IssueNotFound />
      </PageBody>
    );
  }
  return (
    <>
      <CopyCommands identifier={`${project.key}-${number}`} />
      <IssueDetail issueId={issueId} scope={scope} />
    </>
  );
}

/** "Copy link" and "Copy identifier", for an issue that exists. */
function CopyCommands({ identifier }: { identifier: string }) {
  const { t } = useTranslation("issues");

  const commands = useMemo<AppCommand[]>(() => {
    const copy = async (text: string, copied: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast(copied);
      } catch {
        // No clipboard off https, or the browser said no.
        toast.error(t("detail.copyFailed"));
      }
    };
    return [
      {
        id: "issue.copyLink",
        title: t("detail.copyLink"),
        group: "selection",
        icon: Link2,
        keywords: [identifier],
        // The address without the search: it opens the issue and nothing else.
        perform: () =>
          void copy(
            `${window.location.origin}${window.location.pathname}`,
            t("detail.linkCopied"),
          ),
      },
      {
        id: "issue.copyIdentifier",
        title: t("detail.copyIdentifier"),
        group: "selection",
        icon: Hash,
        keywords: [identifier],
        perform: () => void copy(identifier, t("detail.identifierCopied", { identifier })),
      },
    ];
  }, [t, identifier]);
  useCommands(commands);

  return null;
}
